import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import KitchenIcon from "@mui/icons-material/Kitchen";
import LinkIcon from "@mui/icons-material/Link";
import EditIcon from "@mui/icons-material/Edit";
import SettingsIcon from "@mui/icons-material/Settings";
import ChecklistIcon from "@mui/icons-material/Checklist";
import LockIcon from "@mui/icons-material/Lock";
import CampaignIcon from "@mui/icons-material/Campaign";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { selectUser } from "../../features/userSlice";
import { CABINET_COLLABORATOR_PERMISSIONS, CABINET_QUOTE_STATUSES, normalizeCabinetQuoteStatuses } from "./constants";
import { CQ_FIELD, CQ_FIELD_SM, CQ_LABEL, Notice, Segmented, cqStatusClass } from "./ui";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState } from "../../components/UI/States";
import {
  addQuoteCollaborator,
  applyQuoteChecklistTemplate,
  createQuoteChecklistTemplate,
  getCabinetEmployees,
  getCabinetFormTemplateConfig,
  getCabinetQuoteDetail,
  getQuoteChatMessages,
  getQuoteChecklistTemplates,
  getQuoteCollaborators,
  removeQuoteCollaborator,
  sendQuoteChatMessage,
  createQuoteRevision,
  generateCabinetCustomerLink,
  updateCabinetQuote,
  updateQuoteChecklistItem,
  updateQuoteChecklistTemplate,
  updateQuoteCollaboratorPermission,
} from "./api";

const LABEL_CLASS = CQ_LABEL;
const FIELD_CLASS = CQ_FIELD;
const CARD = "app-card rounded-2xl border";
const CARD_TITLE = "text-lg font-semibold tracking-tight";
const DETAIL_LABEL = "app-eyebrow";
// Sections with many columns get smushed on one row; auto-fit keeps each field at a
// usable minimum width and wraps to the next line instead of smashing them together.
const WRAP_GRID_CLASS = "grid gap-4 grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(170px,1fr))]";

const getGridClass = (columns) => {
  if (columns >= 5) return WRAP_GRID_CLASS;
  if (columns === 4) return "grid grid-cols-1 md:grid-cols-4 gap-4";
  if (columns === 3) return "grid grid-cols-1 md:grid-cols-3 gap-4";
  if (columns === 2) return "grid grid-cols-1 md:grid-cols-2 gap-4";
  return "grid grid-cols-1 gap-4";
};

const buildInitialRepeatableRows = (template) =>
  Object.fromEntries(
    (template?.sections || [])
      .filter((section) => section?.repeatable)
      .map((section) => [section.id, [{}]])
  );

const getStatusBadgeClass = cqStatusClass;

const formatStatusLabel = (status) =>
  String(status || "draft")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const Spinner = ({ className = "" }) => (
  <svg className={`animate-spin h-4 w-4 ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
);

const TOAST_STYLES = {
  success: "bg-emerald-600 text-white",
  error: "bg-rose-600 text-white",
  info: "bg-slate-800 text-white",
  warning: "bg-amber-500 text-white",
};

const ToastStack = ({ toasts, onDismiss }) => {
  if (!toasts.length) {
    return null;
  }

  return (
    // Sits below the sticky app header so it never covers the header controls.
    <div className="fixed right-4 top-20 z-[80] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          className={`flex items-start gap-2 rounded-2xl px-4 py-3 text-sm font-medium shadow-lg ring-1 ring-black/5 ${TOAST_STYLES[toast.type] || TOAST_STYLES.info}`}
        >
          <span className="flex-1">{toast.message}</span>
          <button type="button" className="-mr-1 -mt-0.5 rounded-full p-0.5 opacity-80 hover:bg-white/15 hover:opacity-100" onClick={() => onDismiss(toast.id)} aria-label="Dismiss">
            <CloseIcon sx={{ fontSize: 16 }} />
          </button>
        </div>
      ))}
    </div>
  );
};

function CabinetQuoteWorkspace() {
  const { quoteId } = useParams();
  const user = useSelector(selectUser);
  const userId = user?.userID;
  const [quote, setQuote] = useState(null);
  const [collabState, setCollabState] = useState({ owner_user_id: "", owner_name: "", collaborators: [] });
  const [employees, setEmployees] = useState([]);
  const [employeesLoaded, setEmployeesLoaded] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [permission, setPermission] = useState(CABINET_COLLABORATOR_PERMISSIONS[0]);
  const [checklistTemplates, setChecklistTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("default");
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newTemplateItems, setNewTemplateItems] = useState([{ id: 1, label: "", status_on_done: "" }]);
  const [editingTemplateId, setEditingTemplateId] = useState("");
  const [checklistModalOpen, setChecklistModalOpen] = useState(false);
  const [workspaceTab, setWorkspaceTab] = useState("checklist");
  const [chatTab, setChatTab] = useState("internal");
  const [internalMessages, setInternalMessages] = useState([]);
  const [externalMessages, setExternalMessages] = useState([]);
  const [internalDraft, setInternalDraft] = useState("");
  const [externalDraft, setExternalDraft] = useState("");
  const [internalAttachments, setInternalAttachments] = useState([]);
  const [externalAttachments, setExternalAttachments] = useState([]);
  const [imageModal, setImageModal] = useState({ open: false, src: "", name: "" });
  const [toasts, setToasts] = useState([]);
  const [customerPortalLink, setCustomerPortalLink] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingChat, setSendingChat] = useState(false);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [pdfOptionsOpen, setPdfOptionsOpen] = useState(false);
  const [excludedPdfSections, setExcludedPdfSections] = useState([]);
  const [revisionModalOpen, setRevisionModalOpen] = useState(false);
  const [revisionSaving, setRevisionSaving] = useState(false);
  const [revisionNote, setRevisionNote] = useState("");
  const [revisionTitle, setRevisionTitle] = useState("");
  const [revisionCustomerName, setRevisionCustomerName] = useState("");
  const [revisionCustomerPhone, setRevisionCustomerPhone] = useState("");
  const [revisionCustomerEmail, setRevisionCustomerEmail] = useState("");
  const [revisionJobAddress, setRevisionJobAddress] = useState("");
  const [revisionFormConfig, setRevisionFormConfig] = useState({ templates: [] });
  const [revisionSingleValues, setRevisionSingleValues] = useState({});
  const [revisionRepeatableValues, setRevisionRepeatableValues] = useState({});
  const [revisionStepIndex, setRevisionStepIndex] = useState(0);
  const [revisionSectionNotes, setRevisionSectionNotes] = useState({});
  const [quoteStatuses, setQuoteStatuses] = useState([...CABINET_QUOTE_STATUSES]);
  const [statusEditing, setStatusEditing] = useState(false);
  const [pendingStatus, setPendingStatus] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const chatScrollRef = useRef(null);

  const dismissToast = (id) => setToasts((prev) => prev.filter((toast) => toast.id !== id));

  const pushToast = (type, message) => {
    const text = String(message || "").trim();
    if (!text) {
      return;
    }
    const id = `${Date.now()}_${Math.round(Math.random() * 1e6)}`;
    setToasts((prev) => [...prev, { id, type, message: text }]);
    setTimeout(() => dismissToast(id), 4500);
  };

  // Backwards-compatible: existing `setError("msg")` calls now surface as error toasts,
  // while `setError("")` clears (a no-op for the toast model).
  const setError = (message) => pushToast("error", message);
  const notifySuccess = (message) => pushToast("success", message);

  const isOwner = useMemo(() => {
    const currentUserId = String(user?.userID || "").trim().toLowerCase();
    const ownerUserId = String(quote?.owner_user_id || collabState.owner_user_id || "").trim().toLowerCase();
    return Boolean(currentUserId) && Boolean(ownerUserId) && currentUserId === ownerUserId;
  }, [user, quote, collabState]);

  const canReviseQuote = useMemo(() => {
    const currentUserId = String(user?.userID || "").trim().toLowerCase();
    if (!currentUserId) {
      return false;
    }

    const ownerUserId = String(quote?.owner_user_id || collabState.owner_user_id || "").trim().toLowerCase();
    if (ownerUserId && ownerUserId === currentUserId) {
      return true;
    }

    const collaborator = (collabState.collaborators || []).find(
      (entry) => String(entry?.user_id || "").trim().toLowerCase() === currentUserId
    );

    return ["full_access", "quote_revisions_access"].includes(String(collaborator?.permission || ""));
  }, [user, quote, collabState]);


  const canGenerateCustomerPortalLink = useMemo(() => {
    const currentUserId = String(user?.userID || "").trim().toLowerCase();
    if (!currentUserId) {
      return false;
    }

    const ownerUserId = String(quote?.owner_user_id || collabState.owner_user_id || "").trim().toLowerCase();
    if (ownerUserId && ownerUserId === currentUserId) {
      return true;
    }

    const collaborator = (collabState.collaborators || []).find(
      (entry) => String(entry?.user_id || "").trim().toLowerCase() === currentUserId
    );

    return String(collaborator?.permission || "") === "full_access";
  }, [user, quote, collabState]);

  const parseRevisionChatPayload = (message) => {
    if (!message?.text) {
      return null;
    }

    try {
      const parsed = JSON.parse(message.text);
      if (parsed?.type === "cabinet_quote_revision") {
        return parsed;
      }
    } catch (parseError) {
      return null;
    }

    return null;
  };

  const formatRevisionFieldLabel = (fieldPath) => {
    const normalized = String(fieldPath || "")
      .replace(/\./g, " ")
      .replace(/_/g, " ")
      .trim();
    if (!normalized) {
      return "Field";
    }
    return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const revisionTemplate = useMemo(() => {
    const templates = revisionFormConfig?.templates || [];
    if (!Array.isArray(templates) || templates.length === 0) {
      return null;
    }

    const selected = templates.find((template) => template?.id === quote?.selected_form_template_id);
    return selected || templates[0];
  }, [revisionFormConfig, quote]);

  const revisionSteps = useMemo(() => {
    const base = [{ id: "details", title: "Quote Details", hint: "Title, customer info & revision notes" }];
    const sectionSteps = (revisionTemplate?.sections || []).map((section) => ({
      id: `section:${section.id}`,
      title: section.title || "Section",
      section,
    }));
    return [...base, ...sectionSteps];
  }, [revisionTemplate]);

  useEffect(() => {
    setRevisionStepIndex((prev) => Math.min(prev, revisionSteps.length - 1));
  }, [revisionSteps.length]);

  const onRevisionSingleFieldChange = (fieldId, value) => {
    setRevisionSingleValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const onRevisionRepeatableFieldChange = (sectionId, rowIndex, fieldId, value) => {
    setRevisionRepeatableValues((prev) => {
      const rows = [...(prev[sectionId] || [{}])];
      rows[rowIndex] = { ...(rows[rowIndex] || {}), [fieldId]: value };
      return { ...prev, [sectionId]: rows };
    });
  };

  const addRevisionRepeatableRow = (sectionId) => {
    setRevisionRepeatableValues((prev) => ({
      ...prev,
      [sectionId]: [...(prev[sectionId] || [{}]), {}],
    }));
  };

  const removeRevisionRepeatableRow = (sectionId, rowIndex) => {
    setRevisionRepeatableValues((prev) => {
      const next = (prev[sectionId] || []).filter((_, index) => index !== rowIndex);
      return { ...prev, [sectionId]: next.length > 0 ? next : [{}] };
    });
  };

  const openRevisionModal = () => {
    setError("");
    setRevisionTitle(String(quote?.title || ""));
    setRevisionCustomerName(String(quote?.customer?.name || ""));
    setRevisionCustomerPhone(String(quote?.customer?.phone || ""));
    setRevisionCustomerEmail(String(quote?.customer?.email || ""));
    setRevisionJobAddress(String(quote?.customer?.job_address || ""));
    const selectedTemplate =
      (revisionFormConfig?.templates || []).find((template) => template?.id === quote?.selected_form_template_id) ||
      (revisionFormConfig?.templates || [])[0] ||
      null;
    const existingFormValues = quote?.form_values && typeof quote.form_values === "object" ? quote.form_values : {};
    setRevisionSingleValues(existingFormValues?.single && typeof existingFormValues.single === "object" ? existingFormValues.single : {});
    setRevisionRepeatableValues({
      ...buildInitialRepeatableRows(selectedTemplate),
      ...(existingFormValues?.repeatable && typeof existingFormValues.repeatable === "object" ? existingFormValues.repeatable : {}),
    });
    setRevisionSectionNotes(existingFormValues?.notes && typeof existingFormValues.notes === "object" ? existingFormValues.notes : {});
    setRevisionNote("");
    setRevisionStepIndex(0);
    setRevisionModalOpen(true);
  };

  const closeRevisionModal = () => {
    if (revisionSaving) {
      return;
    }
    setRevisionModalOpen(false);
  };

  const refreshQuote = async () => {
    const response = await getCabinetQuoteDetail(quoteId, user?.userID);
    setQuote(response);
  };

  const refreshCollaborators = async () => {
    const data = await getQuoteCollaborators(quoteId, user?.userID);
    setCollabState(data || { owner_user_id: "", owner_name: "", collaborators: [] });
  };

  const refreshChecklistTemplates = async () => {
    const data = await getQuoteChecklistTemplates(quoteId, user?.userID);
    setChecklistTemplates(Array.isArray(data?.templates) ? data.templates : []);
    setSelectedTemplateId(data?.selected_template_id || "default");
  };

  const refreshChats = async () => {
    const [internal, external] = await Promise.all([
      getQuoteChatMessages(quoteId, user?.userID, "internal"),
      getQuoteChatMessages(quoteId, user?.userID, "external"),
    ]);
    setInternalMessages(internal);
    setExternalMessages(external);
  };

  useEffect(() => {
    if (!userId) {
      return;
    }

    refreshQuote();

    refreshCollaborators().catch((loadError) => {
      setError(loadError?.message || "Unable to load collaborators");
    });

    refreshChecklistTemplates().catch((loadError) => {
      setError(loadError?.message || "Unable to load checklist templates");
    });

    refreshChats().catch((loadError) => {
      setError(loadError?.message || "Unable to load chat messages");
    });

    getCabinetFormTemplateConfig(userId)
      .then((config) => {
        setRevisionFormConfig(config || { templates: [] });
        setQuoteStatuses(normalizeCabinetQuoteStatuses(config?.quote_statuses));
      })
      .catch(() => {
        setRevisionFormConfig({ templates: [] });
        setQuoteStatuses([...CABINET_QUOTE_STATUSES]);
      });
  }, [quoteId, userId]);

  useEffect(() => {
    if (workspaceTab !== "collaborators" || !userId || !isOwner || employeesLoaded) {
      return;
    }

    setEmployeesLoaded(true);
    getCabinetEmployees(userId)
      .then((result) => {
        setEmployees(Array.isArray(result) ? result : []);
      })
      .catch(() => {
        setEmployeesLoaded(false);
      });
  }, [workspaceTab, userId, isOwner, employeesLoaded]);

  useEffect(() => {
    if (!quote?.status) {
      return;
    }

    setPendingStatus(quote.status);
    setQuoteStatuses((prev) => normalizeCabinetQuoteStatuses([...(prev || []), quote.status]));
  }, [quote?.status]);

  const availableUsers = useMemo(() => {
    const existing = new Set((collabState.collaborators || []).map((item) => item.user_id));
    existing.add(collabState.owner_user_id);
    return employees.filter((employee) => employee?.id && !existing.has(employee.id));
  }, [employees, collabState]);

  const activeMessages = chatTab === "internal" ? internalMessages : externalMessages;
  const activeDraft = chatTab === "internal" ? internalDraft : externalDraft;
  const activeAttachments = chatTab === "internal" ? internalAttachments : externalAttachments;
  const isExternalChat = chatTab === "external";

  const onSelectChatTab = (tab) => {
    if (tab === chatTab) {
      return;
    }
    setChatTab(tab);
    if (tab === "external") {
      pushToast("warning", "You're now in the customer-facing channel — anything you send here can be seen by the customer.");
    }
  };
  const quotePdfUrl = `/api/forms/pdf?id=${encodeURIComponent(quoteId || "")}&source=cabinet_quote&userId=${encodeURIComponent(user?.userID || "")}`;

  const pdfSectionOptions = [
    { id: "customer_details", title: "Customer Details (status, contact, address)" },
    ...((revisionTemplate?.sections || []).map((section) => ({
      id: section.id,
      title: section.title || "Section",
    }))),
  ];
  const includedPdfSectionIds = pdfSectionOptions
    .filter((section) => !excludedPdfSections.includes(section.id))
    .map((section) => section.id);
  const allPdfSectionsIncluded = excludedPdfSections.length === 0;

  const buildQuotePdfUrl = (download) => {
    let url = quotePdfUrl;
    if (!allPdfSectionsIncluded) {
      url += `&sections=${encodeURIComponent(includedPdfSectionIds.join(",") || "__none__")}`;
    }
    if (download) {
      url += "&download=1";
    }
    return url;
  };

  const togglePdfSection = (sectionId) => {
    setExcludedPdfSections((prev) =>
      prev.includes(sectionId) ? prev.filter((id) => id !== sectionId) : [...prev, sectionId]
    );
  };

  const checklistItems = Array.isArray(quote?.checklist) ? quote.checklist : [];
  const checklistDone = checklistItems.filter((item) => item?.done).length;
  const checklistTotal = checklistItems.length;
  const checklistPercent = checklistTotal > 0 ? Math.round((checklistDone / checklistTotal) * 100) : 0;

  const selectedTemplate = checklistTemplates.find((template) => template.id === selectedTemplateId) || null;
  const selectedTemplateItems = Array.isArray(selectedTemplate?.items) ? selectedTemplate.items : [];
  const isDefaultTemplateSelected = selectedTemplateId === "default";

  const isMyMessage = (message) =>
    Boolean(userId) && String(message?.user_id || "").trim().toLowerCase() === String(userId).trim().toLowerCase();

  const onAddCollaborator = async (event) => {
    event.preventDefault();
    setError("");

    if (!isOwner) {
      setError("Only the quote owner can manage collaborators.");
      return;
    }
    if (!selectedUserId) {
      setError("Select an employee to add.");
      return;
    }

    setSaving(true);
    try {
      await addQuoteCollaborator(quoteId, user?.userID, {
        user_id: selectedUserId,
        permission,
      });
      setSelectedUserId("");
      setPermission(CABINET_COLLABORATOR_PERMISSIONS[0]);
      await refreshCollaborators();
      notifySuccess("Collaborator added.");
    } catch (submitError) {
      setError(submitError?.message || "Failed to add collaborator.");
    } finally {
      setSaving(false);
    }
  };

  const onUpdatePermission = async (collaboratorUserId, nextPermission) => {
    setError("");
    setSaving(true);
    try {
      await updateQuoteCollaboratorPermission(quoteId, user?.userID, collaboratorUserId, nextPermission);
      await refreshCollaborators();
    } catch (submitError) {
      setError(submitError?.message || "Failed to update collaborator permission.");
    } finally {
      setSaving(false);
    }
  };

  const onRemoveCollaborator = async (collaboratorUserId) => {
    setError("");
    setSaving(true);
    try {
      await removeQuoteCollaborator(quoteId, user?.userID, collaboratorUserId);
      await refreshCollaborators();
    } catch (submitError) {
      setError(submitError?.message || "Failed to remove collaborator.");
    } finally {
      setSaving(false);
    }
  };

  const onApplyTemplate = async () => {
    setError("");
    if (!selectedTemplateId) {
      setError("Select a checklist template first.");
      return;
    }
    setSaving(true);
    try {
      await applyQuoteChecklistTemplate(quoteId, user?.userID, selectedTemplateId);
      await refreshQuote();
      await refreshChecklistTemplates();
      setChecklistModalOpen(false);
      notifySuccess("Checklist template applied.");
    } catch (applyError) {
      setError(applyError?.message || "Failed to apply checklist template.");
    } finally {
      setSaving(false);
    }
  };

  const onTemplateItemChange = (itemId, patch) => {
    setNewTemplateItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, ...patch } : item)));
  };

  const onTemplateItemAdd = () => {
    setNewTemplateItems((prev) => [...prev, { id: Date.now() + Math.random(), label: "", status_on_done: "" }]);
  };

  const onTemplateItemRemove = (itemId) => {
    setNewTemplateItems((prev) => {
      if (prev.length <= 1) {
        return prev;
      }
      return prev.filter((item) => item.id !== itemId);
    });
  };

  const onEditSelectedTemplate = () => {
    setError("");
    const template = checklistTemplates.find((entry) => entry.id === selectedTemplateId);
    if (!template) {
      setError("Select a template first.");
      return;
    }

    if (template.id === "default") {
      setError("Default template cannot be edited.");
      return;
    }

    if (!isOwner || String(template.owner_user_id || "").trim().toLowerCase() !== String(user?.userID || "").trim().toLowerCase()) {
      setError("You can only edit your own checklist templates.");
      return;
    }

    setEditingTemplateId(template.id);
    setNewTemplateName(template.name || "");
    const items = (Array.isArray(template.items) ? template.items : []).map((item, index) => ({
      id: Date.now() + index + Math.random(),
      label: item?.label || "",
      status_on_done: item?.status_on_done || "",
    }));
    setNewTemplateItems(items.length > 0 ? items : [{ id: Date.now(), label: "", status_on_done: "" }]);
  };

  const onCancelTemplateEdit = () => {
    setEditingTemplateId("");
    setNewTemplateName("");
    setNewTemplateItems([{ id: 1, label: "", status_on_done: "" }]);
  };

  const onCreateTemplate = async (event) => {
    event.preventDefault();
    setError("");
    if (!isOwner) {
      setError("Only the quote owner can create checklist templates.");
      return;
    }

    const items = newTemplateItems
      .map((item) => ({
        label: String(item.label || "").trim(),
        status_on_done: String(item.status_on_done || "").trim() || null,
      }))
      .filter((item) => item.label);

    if (!newTemplateName.trim() || items.length === 0) {
      setError("Template name and at least one checklist line are required.");
      return;
    }

    setSaving(true);
    try {
      if (editingTemplateId) {
        const updated = await updateQuoteChecklistTemplate(quoteId, user?.userID, editingTemplateId, {
          name: newTemplateName.trim(),
          items,
        });
        await refreshChecklistTemplates();
        setSelectedTemplateId(updated?.id || editingTemplateId);
      } else {
        const created = await createQuoteChecklistTemplate(quoteId, user?.userID, {
          name: newTemplateName.trim(),
          items,
        });
        await refreshChecklistTemplates();
        setSelectedTemplateId(created?.id || "default");
      }

      notifySuccess(editingTemplateId ? "Checklist template updated." : "Checklist template saved.");
      setEditingTemplateId("");
      setNewTemplateName("");
      setNewTemplateItems([{ id: 1, label: "", status_on_done: "" }]);
    } catch (createError) {
      setError(createError?.message || `Failed to ${editingTemplateId ? "update" : "create"} checklist template.`);
    } finally {
      setSaving(false);
    }
  };

  const onToggleChecklist = async (itemId, done) => {
    // Optimistic flip so checking items feels instant and doesn't lock the whole list.
    setQuote((prev) => {
      if (!prev) {
        return prev;
      }
      const checklist = (Array.isArray(prev.checklist) ? prev.checklist : []).map((item) =>
        item.id === itemId ? { ...item, done } : item
      );
      return { ...prev, checklist };
    });

    try {
      await updateQuoteChecklistItem(quoteId, user?.userID, itemId, { done });
      // Resync in case completing an item advanced the quote status server-side.
      await refreshQuote();
    } catch (toggleError) {
      setError(toggleError?.message || "Failed to update checklist item.");
      refreshQuote().catch(() => {});
    }
  };

  const onStartStatusEdit = () => {
    setError("");
    setPendingStatus(quote?.status || quoteStatuses[0] || "draft");
    setStatusEditing(true);
  };

  const onCancelStatusEdit = () => {
    setPendingStatus(quote?.status || "draft");
    setStatusEditing(false);
  };

  const onUpdateQuoteStatus = async () => {
    if (!pendingStatus || pendingStatus === quote?.status) {
      setStatusEditing(false);
      return;
    }

    setError("");
    setStatusSaving(true);
    try {
      await updateCabinetQuote(quoteId, user?.userID, { status: pendingStatus });
      await refreshQuote();
      setStatusEditing(false);
      notifySuccess(`Status updated to "${formatStatusLabel(pendingStatus)}".`);
    } catch (statusError) {
      setError(statusError?.message || "Failed to update quote status.");
    } finally {
      setStatusSaving(false);
    }
  };

  const formatChatTimestamp = (value) => {
    if (!value) {
      return "";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const onSendChatMessage = async () => {
    const text = String(activeDraft || "").trim();
    if (!text && activeAttachments.length === 0) {
      return;
    }

    setError("");
    setSendingChat(true);
    try {
      await sendQuoteChatMessage(quoteId, user?.userID, chatTab, {
        text,
        user_name: user?.name || user?.fullName || user?.displayName || user?.userID,
        attachments: activeAttachments,
      });

      if (chatTab === "internal") {
        setInternalDraft("");
        setInternalAttachments([]);
      } else {
        setExternalDraft("");
        setExternalAttachments([]);
      }

      await refreshChats();
    } catch (sendError) {
      setError(sendError?.message || "Failed to send chat message.");
    } finally {
      setSendingChat(false);
    }
  };

  const onSelectChatAttachments = (event) => {
    const files = Array.from(event.target?.files || []);
    const valid = files.filter((file) => {
      const mime = String(file?.type || "").toLowerCase();
      return mime === "application/pdf" || mime.startsWith("image/");
    });
    if (chatTab === "internal") {
      setInternalAttachments(valid);
    } else {
      setExternalAttachments(valid);
    }
    event.target.value = "";
  };

  const removeActiveAttachment = (index) => {
    if (chatTab === "internal") {
      setInternalAttachments((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    } else {
      setExternalAttachments((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    }
  };

  const withUserIdQuery = (url) => `${url}${url.includes("?") ? "&" : "?"}userId=${encodeURIComponent(user?.userID || "")}`;
  const withDownloadQuery = (url) => `${withUserIdQuery(url)}&download=1`;

  const onSubmitRevision = async (event) => {
    event.preventDefault();
    if (!canReviseQuote) {
      setError("You do not have permission to submit revisions.");
      return;
    }

    const nextPayload = {
      title: revisionTitle.trim(),
      customer: {
        name: revisionCustomerName.trim(),
        phone: revisionCustomerPhone.trim(),
        email: revisionCustomerEmail.trim(),
        job_address: revisionJobAddress.trim(),
      },
      form_values: {
        single: revisionSingleValues,
        repeatable: revisionRepeatableValues,
        notes: revisionSectionNotes,
      },
    };

    const updatedFields = [];
    if (nextPayload.title !== String(quote?.title || "").trim()) {
      updatedFields.push({ field: "title", from: String(quote?.title || ""), to: nextPayload.title });
    }

    const currentCustomer = quote?.customer || {};
    [
      ["customer.name", String(currentCustomer?.name || ""), nextPayload.customer.name],
      ["customer.phone", String(currentCustomer?.phone || ""), nextPayload.customer.phone],
      ["customer.email", String(currentCustomer?.email || ""), nextPayload.customer.email],
      ["customer.job_address", String(currentCustomer?.job_address || ""), nextPayload.customer.job_address],
    ].forEach(([field, currentValue, nextValue]) => {
      if (currentValue !== nextValue) {
        updatedFields.push({ field, from: currentValue, to: nextValue });
      }
    });

    const currentFormValues = quote?.form_values && typeof quote.form_values === "object" ? quote.form_values : {};
    const currentSingle = currentFormValues?.single && typeof currentFormValues.single === "object" ? currentFormValues.single : {};
    const currentRepeatable = currentFormValues?.repeatable && typeof currentFormValues.repeatable === "object" ? currentFormValues.repeatable : {};
    const templateSections = revisionTemplate?.sections || [];

    templateSections.forEach((section) => {
      const sectionTitle = String(section?.title || "Section").trim();
      const fields = section?.fields || [];

      if (section?.repeatable) {
        const currentRows = Array.isArray(currentRepeatable?.[section.id]) ? currentRepeatable[section.id] : [];
        const nextRows = Array.isArray(revisionRepeatableValues?.[section.id]) ? revisionRepeatableValues[section.id] : [];
        const totalRows = Math.max(currentRows.length, nextRows.length, 0);

        for (let rowIndex = 0; rowIndex < totalRows; rowIndex += 1) {
          fields.forEach((field) => {
            const fieldLabel = String(field?.label || field?.id || "Field").trim();
            const currentValue = String(currentRows?.[rowIndex]?.[field.id] || "");
            const nextValue = String(nextRows?.[rowIndex]?.[field.id] || "");
            if (currentValue !== nextValue) {
              updatedFields.push({
                field: `${sectionTitle} Row ${rowIndex + 1} - ${fieldLabel}`,
                from: currentValue,
                to: nextValue,
              });
            }
          });
        }
        return;
      }

      fields.forEach((field) => {
        const fieldLabel = String(field?.label || field?.id || "Field").trim();
        const currentValue = String(currentSingle?.[field.id] || "");
        const nextValue = String(revisionSingleValues?.[field.id] || "");
        if (currentValue !== nextValue) {
          updatedFields.push({
            field: `${sectionTitle} - ${fieldLabel}`,
            from: currentValue,
            to: nextValue,
          });
        }
      });
    });

    if (updatedFields.length === 0 && !revisionNote.trim()) {
      setError("Please update at least one field or add revision notes.");
      return;
    }

    setError("");
    setRevisionSaving(true);
    try {
      await createQuoteRevision(quoteId, user?.userID, {
        note: revisionNote.trim(),
        updated_fields: updatedFields,
        quote_updates: nextPayload,
        user_name: user?.name || user?.fullName || user?.displayName || user?.userID,
      });
      await Promise.all([refreshQuote(), refreshChats()]);
      setRevisionModalOpen(false);
      notifySuccess("Revision submitted.");
    } catch (submitError) {
      setError(submitError?.message || "Failed to submit revision.");
    } finally {
      setRevisionSaving(false);
    }
  };


  const onGenerateCustomerPortalLink = async () => {
    if (!canGenerateCustomerPortalLink) {
      setError("Only owners or full access collaborators can generate a customer link.");
      return;
    }

    setError("");
    setSaving(true);
    try {
      const response = await generateCabinetCustomerLink(quoteId, user?.userID);
      const path = String(response?.path || "").trim();
      if (!path) {
        throw new Error("Failed to generate customer link");
      }
      const absoluteLink = `${window.location.origin}${path}`;
      setCustomerPortalLink(absoluteLink);
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(absoluteLink);
        notifySuccess("Customer link generated and copied to clipboard.");
      } else {
        notifySuccess("Customer link generated.");
      }
    } catch (submitError) {
      setError(submitError?.message || "Failed to generate customer link.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!chatScrollRef.current) {
      return;
    }

    chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [activeMessages, chatTab, chatExpanded]);

  if (!quote) {
    return (
      <PageShell size="2xl">
        <ToastStack toasts={toasts} onDismiss={dismissToast} />
        <div role="status" aria-label="Loading quote">
          <div className="mb-6 flex items-start gap-4">
            <div className="app-skeleton h-12 w-12 !rounded-2xl" />
            <div className="space-y-2 pt-1">
              <div className="app-skeleton h-3 w-20" />
              <div className="app-skeleton h-7 w-64 max-w-[60vw]" />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] xl:grid-cols-[minmax(0,1fr)_22rem]">
            <div className={`${CARD} space-y-3 p-5 lg:col-start-2 lg:row-start-1`}>
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="app-skeleton h-9 w-full" />
              ))}
            </div>
            <div className="space-y-6 lg:col-start-1 lg:row-start-1">
              <div className={`${CARD} h-72 p-5`}>
                <div className="app-skeleton h-full w-full" />
              </div>
              <div className={`${CARD} h-72 p-5`}>
                <div className="app-skeleton h-full w-full" />
              </div>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  const safeRevIndex = Math.max(0, Math.min(revisionStepIndex, revisionSteps.length - 1));
  const currentRevisionStep = revisionSteps[safeRevIndex] || revisionSteps[0];
  const isLastRevisionStep = safeRevIndex === revisionSteps.length - 1;
  const goPrevRevision = () => setRevisionStepIndex((prev) => Math.max(0, prev - 1));
  const goNextRevision = () => setRevisionStepIndex((prev) => Math.min(revisionSteps.length - 1, prev + 1));

  const renderRevisionSectionFields = (section) =>
    section.repeatable ? (
      <div className="space-y-3">
        {(revisionRepeatableValues[section.id] || [{}]).map((row, rowIndex) => (
          <div key={`${section.id}_${rowIndex}`} className="app-card space-y-3 rounded-xl border p-4">
            <div className="flex items-center justify-between">
              <span className="app-eyebrow tabular-nums">Row {rowIndex + 1}</span>
              <button className="app-btn app-btn-ghost app-btn-sm !text-rose-700 dark:!text-rose-300" type="button" onClick={() => removeRevisionRepeatableRow(section.id, rowIndex)} disabled={revisionSaving}>
                Remove Row
              </button>
            </div>
            <div className={getGridClass(section.columns)}>
              {(section.fields || []).map((field) => (
                <div key={`${section.id}_${field.id}_${rowIndex}`}>
                  <label className={LABEL_CLASS}>{field.label}{field.required ? " *" : ""}</label>
                  {field.type === "select" ? (
                    <select className={FIELD_CLASS} value={row[field.id] || ""} onChange={(event) => onRevisionRepeatableFieldChange(section.id, rowIndex, field.id, event.target.value)} disabled={revisionSaving}>
                      <option value="">Select an option</option>
                      {(field.options || []).map((option) => (
                        <option key={`${field.id}_${option}`} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <input className={FIELD_CLASS} value={row[field.id] || ""} onChange={(event) => onRevisionRepeatableFieldChange(section.id, rowIndex, field.id, event.target.value)} placeholder={field.label} disabled={revisionSaving} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className={getGridClass(section.columns)}>
        {(section.fields || []).map((field) => (
          <div key={`${section.id}_${field.id}`}>
            <label className={LABEL_CLASS}>{field.label}{field.required ? " *" : ""}</label>
            {field.type === "select" ? (
              <select className={FIELD_CLASS} value={revisionSingleValues[field.id] || ""} onChange={(event) => onRevisionSingleFieldChange(field.id, event.target.value)} disabled={revisionSaving}>
                <option value="">Select an option</option>
                {(field.options || []).map((option) => (
                  <option key={`${field.id}_${option}`} value={option}>{option}</option>
                ))}
              </select>
            ) : (
              <input className={FIELD_CLASS} value={revisionSingleValues[field.id] || ""} onChange={(event) => onRevisionSingleFieldChange(field.id, event.target.value)} placeholder={field.label} disabled={revisionSaving} />
            )}
          </div>
        ))}
      </div>
    );

  const renderActiveRevisionStep = () => {
    if (currentRevisionStep?.id === "details") {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className={LABEL_CLASS}>Quote Title</label>
              <input className={FIELD_CLASS} value={revisionTitle} onChange={(event) => setRevisionTitle(event.target.value)} disabled={revisionSaving} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Customer Name</label>
              <input className={FIELD_CLASS} value={revisionCustomerName} onChange={(event) => setRevisionCustomerName(event.target.value)} disabled={revisionSaving} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Customer Phone</label>
              <input className={FIELD_CLASS} value={revisionCustomerPhone} onChange={(event) => setRevisionCustomerPhone(event.target.value)} disabled={revisionSaving} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Customer Email</label>
              <input className={FIELD_CLASS} value={revisionCustomerEmail} onChange={(event) => setRevisionCustomerEmail(event.target.value)} disabled={revisionSaving} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Job Address</label>
              <input className={FIELD_CLASS} value={revisionJobAddress} onChange={(event) => setRevisionJobAddress(event.target.value)} disabled={revisionSaving} />
            </div>
          </div>
          <div>
            <label className={LABEL_CLASS}>Revision Notes</label>
            <textarea
              className={`${FIELD_CLASS} resize-y`}
              value={revisionNote}
              onChange={(event) => setRevisionNote(event.target.value)}
              rows={4}
              placeholder="Explain what changed in this revision (shared in the revision history)"
              disabled={revisionSaving}
            />
          </div>
        </div>
      );
    }

    if (currentRevisionStep?.section) {
      return renderRevisionSectionFields(currentRevisionStep.section);
    }

    return null;
  };

  const noPdfSections = includedPdfSectionIds.length === 0;

  const detailRows = [
    { label: "Rep", value: `${quote.owner_name || quote.owner_user_id} ${isOwner ? "(you)" : ""}` },
    { label: "Customer", value: quote?.customer?.name || "N/A" },
    { label: "Phone", value: quote?.customer?.phone || "N/A", className: "tabular-nums" },
    { label: "Email", value: quote?.customer?.email || "N/A", className: "break-all" },
    { label: "Job Address", value: quote?.customer?.job_address || "N/A" },
  ];

  const stepPillClass = (isCurrent) =>
    `flex min-h-[2.5rem] shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-60 ${
      isCurrent
        ? "border-transparent bg-[color:var(--app-accent-solid)] text-white shadow-sm"
        : "app-surface hover:bg-[color:var(--app-hover)]"
    }`;

  return (
    <PageShell size="2xl">
      <ToastStack toasts={toasts} onDismiss={dismissToast} />

      <PageHeader
        icon={KitchenIcon}
        tone="amber"
        eyebrow="Cabinets"
        title={quote.title || "Cabinet Quote"}
        description={quote.quote_number ? `Quote #${quote.quote_number}` : undefined}
        actions={
          <>
            <button
              type="button"
              className="app-btn app-btn-secondary"
              onClick={openRevisionModal}
              disabled={!canReviseQuote}
              title={canReviseQuote ? "Start Revision" : "You do not have revision permission"}
            >
              <EditIcon sx={{ fontSize: 18 }} />
              Start Revision
            </button>
            <button
              type="button"
              className="app-btn app-btn-primary"
              disabled={!canGenerateCustomerPortalLink || saving}
              onClick={onGenerateCustomerPortalLink}
              title={canGenerateCustomerPortalLink ? "Generate secure customer portal link" : "Only owner/full access can generate"}
            >
              {saving ? <Spinner className="text-white" /> : <LinkIcon sx={{ fontSize: 18 }} />}
              Generate Customer Link
            </button>
          </>
        }
      >
        {(!canReviseQuote || customerPortalLink) && (
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
            {!canReviseQuote && (
              <span className="text-xs font-medium text-amber-700 dark:text-amber-300 sm:mr-auto">You can't submit revisions for this quote.</span>
            )}
            {customerPortalLink && (
              <div className="app-surface flex min-w-0 items-center gap-2 rounded-xl border p-1.5 pl-3 text-sm sm:max-w-lg sm:flex-1">
                <LinkIcon fontSize="small" className="app-muted shrink-0" />
                <input
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                  readOnly
                  aria-label="Customer portal link"
                  value={customerPortalLink}
                />
                <button
                  type="button"
                  className="app-btn app-btn-ghost app-btn-sm shrink-0"
                  onClick={() => {
                    if (navigator?.clipboard?.writeText) {
                      navigator.clipboard.writeText(customerPortalLink);
                      notifySuccess("Customer link copied to clipboard.");
                    }
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                  Copy
                </button>
                <a className="app-btn app-btn-ghost app-btn-sm shrink-0" href={customerPortalLink} target="_blank" rel="noreferrer">
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                  Open
                </a>
              </div>
            )}
          </div>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] xl:grid-cols-[minmax(0,1fr)_22rem]">
        {/* Side panel: status + customer details. First on phones, right rail on desktop. */}
        <aside className="space-y-6 lg:sticky lg:top-24 lg:col-start-2 lg:row-start-1 lg:self-start">
          <section className={`${CARD} p-5`}>
            <p className={DETAIL_LABEL}>Status</p>
            {statusEditing ? (
              <div className="mt-2 space-y-2">
                <select
                  className={CQ_FIELD}
                  aria-label="Quote status"
                  value={pendingStatus}
                  onChange={(event) => setPendingStatus(event.target.value)}
                  disabled={statusSaving}
                >
                  {quoteStatuses.map((status) => (
                    <option key={status} value={status}>
                      {formatStatusLabel(status)}
                    </option>
                  ))}
                </select>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="app-btn app-btn-primary app-btn-sm"
                    onClick={onUpdateQuoteStatus}
                    disabled={statusSaving}
                  >
                    {statusSaving && <Spinner className="text-white" />}
                    {statusSaving ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    className="app-btn app-btn-ghost app-btn-sm"
                    onClick={onCancelStatusEdit}
                    disabled={statusSaving}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className={`group mt-2 inline-flex w-fit items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-opacity hover:opacity-90 ${getStatusBadgeClass(quote.status)}`}
                onClick={onStartStatusEdit}
                title="Click to change status"
              >
                <span className="h-2 w-2 rounded-full bg-current opacity-70" />
                {formatStatusLabel(quote.status)}
                <EditIcon sx={{ fontSize: 14 }} className="opacity-50 group-hover:opacity-90" />
              </button>
            )}

            <dl className="app-divider mt-5 space-y-3.5 border-t pt-4 text-sm">
              {detailRows.map((row) => (
                <div key={row.label}>
                  <dt className={DETAIL_LABEL}>{row.label}</dt>
                  <dd className={`mt-0.5 font-medium ${row.className || ""}`}>{row.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        </aside>

        <div className="min-w-0 space-y-6 lg:col-start-1 lg:row-start-1">
          <section className={`${CARD} space-y-4 p-5`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className={CARD_TITLE}>Quote Workflow</h2>
              {workspaceTab === "checklist" && (
                <button
                  type="button"
                  className="app-btn app-btn-secondary app-btn-sm"
                  onClick={() => setChecklistModalOpen(true)}
                  disabled={saving}
                  title="Apply or edit checklist templates"
                >
                  <SettingsIcon sx={{ fontSize: 16 }} />
                  Templates
                </button>
              )}
            </div>

            <Segmented
              ariaLabel="Workflow view"
              value={workspaceTab}
              onChange={setWorkspaceTab}
              options={[
                { value: "checklist", label: "Checklist" },
                { value: "collaborators", label: "Collaborators" },
              ]}
            />

            {workspaceTab === "checklist" ? (
              checklistTotal > 0 ? (
                <>
                  <div className="space-y-1.5">
                    <div className="app-muted flex items-center justify-between text-xs tabular-nums">
                      <span>{checklistDone} of {checklistTotal} complete</span>
                      <span className="font-semibold">{checklistPercent}%</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-[color:var(--app-pressed)]">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${checklistPercent === 100 ? "bg-emerald-500" : "bg-sky-500"}`}
                        style={{ width: `${checklistPercent}%` }}
                      />
                    </div>
                  </div>
                  <ul className="app-surface divide-y divide-[color:var(--app-divider)] overflow-hidden rounded-xl border">
                    {checklistItems.map((item) => (
                      <li key={item.id} className={`transition-colors ${item.done ? "bg-emerald-500/10" : "hover:bg-[color:var(--app-hover)]"}`}>
                        <label className="flex min-h-[2.75rem] cursor-pointer items-start gap-3 p-3">
                          <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-600"
                            checked={Boolean(item.done)}
                            onChange={(event) => onToggleChecklist(item.id, event.target.checked)}
                          />
                          <span className="min-w-0">
                            <span className={`block text-sm ${item.done ? "app-muted line-through" : ""}`}>{item.label}</span>
                            {item.status_on_done && (
                              <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 text-[11px] font-medium text-sky-700 dark:text-sky-300">
                                → Sets status: {formatStatusLabel(item.status_on_done)}
                              </span>
                            )}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <div className="app-surface rounded-xl border border-dashed">
                  <EmptyState
                    compact
                    icon={ChecklistIcon}
                    title="No checklist yet"
                    description="Apply a template to start tracking this quote's progress."
                    action={
                      <button
                        type="button"
                        className="app-btn app-btn-primary"
                        onClick={() => setChecklistModalOpen(true)}
                      >
                        <SettingsIcon sx={{ fontSize: 16 }} />
                        Choose a Template
                      </button>
                    }
                  />
                </div>
              )
            ) : (
              <>
                <p className="app-muted text-sm">Manage who can collaborate on this quote and what access level they have.</p>

                <div className="app-surface rounded-xl border px-3 py-2 text-sm">
                  <strong>Owner:</strong> {collabState.owner_name || quote.owner_name || quote.owner_user_id}
                </div>

                <ul className="space-y-2 text-sm">
                  {(collabState.collaborators || []).map((person) => (
                    <li key={person.user_id} className="app-surface rounded-xl border px-3 py-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium">{person.display_name || person.user_id}</p>
                          <p className="app-muted text-xs">{person.user_id}</p>
                        </div>

                        {isOwner ? (
                          <div className="flex items-center gap-2">
                            <select
                              className={CQ_FIELD_SM}
                              aria-label={`Permission for ${person.display_name || person.user_id}`}
                              value={person.permission}
                              disabled={saving}
                              onChange={(event) => onUpdatePermission(person.user_id, event.target.value)}
                            >
                              {CABINET_COLLABORATOR_PERMISSIONS.map((level) => (
                                <option key={level} value={level}>
                                  {level}
                                </option>
                              ))}
                            </select>
                            <button
                              className="app-btn app-btn-sm bg-rose-500/10 text-rose-700 hover:bg-rose-500/20 dark:text-rose-300"
                              type="button"
                              disabled={saving}
                              onClick={() => onRemoveCollaborator(person.user_id)}
                            >
                              Remove
                            </button>
                          </div>
                        ) : (
                          <span className="app-chip">{person.permission}</span>
                        )}
                      </div>
                    </li>
                  ))}
                  {(collabState.collaborators || []).length === 0 && <li className="app-muted text-sm">No collaborators assigned yet.</li>}
                </ul>

                {isOwner && (
                  <form className="app-divider space-y-3 border-t pt-4" onSubmit={onAddCollaborator}>
                    <h3 className="text-sm font-semibold">Add Collaborator</h3>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <select
                        className={CQ_FIELD}
                        aria-label="Employee"
                        value={selectedUserId}
                        onChange={(event) => setSelectedUserId(event.target.value)}
                        disabled={saving}
                      >
                        <option value="">Select employee</option>
                        {availableUsers.map((employee) => (
                          <option key={employee.id} value={employee.id}>
                            {employee.name || employee.id} {employee.title ? `(${employee.title})` : ""}
                          </option>
                        ))}
                      </select>
                      <select
                        className={CQ_FIELD}
                        aria-label="Permission"
                        value={permission}
                        onChange={(event) => setPermission(event.target.value)}
                        disabled={saving}
                      >
                        {CABINET_COLLABORATOR_PERMISSIONS.map((level) => (
                          <option key={level} value={level}>
                            {level}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button className="app-btn app-btn-primary" type="submit" disabled={saving}>
                      Add Collaborator
                    </button>
                  </form>
                )}
              </>
            )}
          </section>

          <section className={`${CARD} space-y-3 p-5 transition-shadow ${isExternalChat ? "ring-2 ring-amber-400/80" : ""}`}>
            <div className="flex items-center justify-between gap-2">
              <h2 className={CARD_TITLE}>Quote Chat</h2>
              <button
                type="button"
                className="app-btn app-btn-ghost app-btn-sm"
                onClick={() => setChatExpanded((prev) => !prev)}
                aria-expanded={chatExpanded}
              >
                {chatExpanded ? <UnfoldLessIcon sx={{ fontSize: 16 }} /> : <UnfoldMoreIcon sx={{ fontSize: 16 }} />}
                {chatExpanded ? "Collapse" : "Expand"}
              </button>
            </div>

            <Segmented
              ariaLabel="Chat channel"
              value={chatTab}
              onChange={onSelectChatTab}
              options={[
                { value: "internal", label: "Internal", icon: <LockIcon sx={{ fontSize: 15 }} /> },
                {
                  value: "external",
                  label: "External / Customer",
                  icon: <CampaignIcon sx={{ fontSize: 16 }} />,
                  activeClass: "bg-amber-500 text-white shadow-sm",
                  inactiveClass: "text-amber-700 hover:text-amber-800 dark:text-amber-300 dark:hover:text-amber-200",
                },
              ]}
            />

            {isExternalChat ? (
              <Notice tone="amber" className="flex items-start gap-2">
                <WarningAmberIcon sx={{ fontSize: 18 }} className="mt-px shrink-0" />
                <span><strong>Customer-facing channel.</strong> Anything you send here can be seen by the customer. Switch to Internal for private notes.</span>
              </Notice>
            ) : (
              <Notice tone="slate" className="app-muted flex items-start gap-2">
                <LockIcon sx={{ fontSize: 16 }} className="mt-0.5 shrink-0" />
                <span><strong>Private channel.</strong> Only quote owners and collaborators can see these messages.</span>
              </Notice>
            )}

            <div
              ref={chatScrollRef}
              className={`space-y-2 overflow-y-auto rounded-xl border p-3 ${isExternalChat ? "border-amber-500/30 bg-amber-500/[0.06]" : "app-surface"} ${chatExpanded ? "h-[32rem]" : "h-80"}`}
            >
              {activeMessages.map((message) => {
                const mine = isMyMessage(message);
                const revisionPayload = parseRevisionChatPayload(message);
                return (
                <div key={message.id} className={`flex ${!revisionPayload && mine ? "justify-end" : "justify-start"}`}>
                <div
                  className={`rounded-2xl border px-3 py-2 text-sm ${
                    revisionPayload
                      ? "app-card w-full"
                      : `max-w-[85%] ${mine ? "rounded-br-md border-sky-500/25 bg-sky-500/15" : "app-card rounded-bl-md"}`
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold">
                      {message.user_name || message.user_id}
                      {mine && !revisionPayload ? " (you)" : ""}
                    </p>
                    <p className="app-muted text-[11px] tabular-nums">{formatChatTimestamp(message.created_at)}</p>
                  </div>
                  {(() => {
                    if (!revisionPayload) {
                      return (
                        <div className="mt-1 space-y-2">
                          {message.text ? <p className="whitespace-pre-wrap break-words">{message.text}</p> : null}
                          {Array.isArray(message?.attachments) && message.attachments.length > 0 ? (
                            <div className="space-y-2">
                              {message.attachments.map((attachment) => {
                                if (!attachment?.url) {
                                  return null;
                                }
                                if (attachment?.kind === "pdf") {
                                  return (
                                    <a
                                      key={attachment.id}
                                      href={withDownloadQuery(attachment.url)}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="app-link inline-flex items-center gap-2 text-sm"
                                    >
                                      📄 {attachment.name || "PDF"}
                                    </a>
                                  );
                                }

                                return (
                                  <div key={attachment.id} className="inline-flex flex-col gap-1">
                                    <button
                                      type="button"
                                      className="text-left"
                                      onClick={() =>
                                        setImageModal({ open: true, src: withUserIdQuery(attachment.url), name: attachment.name || "Image" })
                                      }
                                      aria-label={`View ${attachment.name || "image"}`}
                                    >
                                      <img
                                        src={withUserIdQuery(attachment.url)}
                                        alt={attachment.name || "Chat attachment"}
                                        className="app-divider max-h-48 rounded-xl border object-cover"
                                      />
                                    </button>
                                    <a
                                      href={withDownloadQuery(attachment.url)}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="app-link text-xs"
                                    >
                                      Download image
                                    </a>
                                  </div>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      );
                    }

                    return (
                      <div className="mt-1.5 space-y-2 rounded-xl border border-sky-500/25 bg-sky-500/10 p-2.5">
                        <p className="text-xs font-semibold text-sky-800 dark:text-sky-200">
                          Revision submitted by {revisionPayload.byUserName || revisionPayload.byUserId || "Unknown"}
                        </p>
                        {Array.isArray(revisionPayload.updatedFields) && revisionPayload.updatedFields.length > 0 ? (
                          <ul className="ml-5 list-disc space-y-1 text-xs text-sky-900 dark:text-sky-100">
                            {revisionPayload.updatedFields.map((change, index) => (
                              <li key={`${revisionPayload.revisionId}_${index}`}>
                                <strong>{formatRevisionFieldLabel(change?.field)}:</strong> "{String(change?.from || "")}" → "{String(change?.to || "")}"
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs text-sky-900 dark:text-sky-100">No field changes were provided.</p>
                        )}
                        {revisionPayload.note ? (
                          <p className="text-xs text-sky-900 dark:text-sky-100">
                            <strong>Notes:</strong> {revisionPayload.note}
                          </p>
                        ) : null}
                      </div>
                    );
                  })()}
                </div>
                </div>
                );
              })}
              {activeMessages.length === 0 && (
                <div className="flex h-full items-center justify-center text-center">
                  <p className="app-muted text-sm">No messages yet.</p>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                className={`${CQ_FIELD} sm:flex-1 ${isExternalChat ? "!border-amber-500/60 !bg-amber-500/[0.06]" : ""}`}
                aria-label={isExternalChat ? "Message to the customer" : "Internal message"}
                value={activeDraft}
                onChange={(event) => {
                  if (chatTab === "internal") {
                    setInternalDraft(event.target.value);
                  } else {
                    setExternalDraft(event.target.value);
                  }
                }}
                placeholder={isExternalChat ? "Message to the customer…" : "Type an internal message"}
                disabled={sendingChat}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    onSendChatMessage();
                  }
                }}
              />
              <div className="flex gap-2 sm:shrink-0">
                <label className="app-btn app-btn-secondary cursor-pointer">
                  <AttachFileIcon sx={{ fontSize: 16 }} />
                  Attach
                  <input type="file" className="hidden" accept="application/pdf,image/*" multiple onChange={onSelectChatAttachments} />
                </label>
                <button
                  className={`app-btn flex-1 sm:flex-none ${isExternalChat ? "bg-amber-600 text-white hover:bg-amber-700" : "app-btn-primary"}`}
                  type="button"
                  onClick={onSendChatMessage}
                  disabled={sendingChat}
                >
                  {sendingChat && <Spinner className="text-white" />}
                  {sendingChat ? "Sending..." : isExternalChat ? "Send to customer" : "Send"}
                </button>
              </div>
            </div>
            {activeAttachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {activeAttachments.map((file, index) => (
                  <span key={`${file.name}_${index}`} className="app-chip max-w-full py-1 pr-1">
                    <span className="truncate">{file.name}</span>
                    <button
                      type="button"
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-rose-700 hover:bg-rose-500/10 dark:text-rose-300"
                      onClick={() => removeActiveAttachment(index)}
                      aria-label={`Remove ${file.name}`}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </section>

          <section className={`${CARD} space-y-4 p-5`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className={CARD_TITLE}>Submitted Form PDF</h2>
                <p className="app-muted text-sm">Download or open the PDF to share.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <a
                  href={buildQuotePdfUrl(true)}
                  aria-disabled={noPdfSections}
                  className={`app-btn app-btn-primary ${noPdfSections ? "pointer-events-none" : ""}`}
                >
                  <DownloadIcon sx={{ fontSize: 18 }} />
                  Download
                </a>
                <a
                  href={buildQuotePdfUrl(false)}
                  target="_blank"
                  rel="noreferrer"
                  aria-disabled={noPdfSections}
                  className={`app-btn app-btn-secondary ${noPdfSections ? "pointer-events-none" : ""}`}
                >
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                  Open
                </a>
              </div>
            </div>

            <div className="app-surface overflow-hidden rounded-xl border">
              <button
                type="button"
                className="flex min-h-[2.75rem] w-full items-center justify-between gap-2 px-3 py-2 text-sm transition-colors hover:bg-[color:var(--app-hover)]"
                onClick={() => setPdfOptionsOpen((prev) => !prev)}
                aria-expanded={pdfOptionsOpen}
              >
                <span className="flex items-center gap-2 font-medium">
                  <ChevronRightIcon fontSize="small" className={`app-muted transition-transform ${pdfOptionsOpen ? "rotate-90" : ""}`} />
                  Sections to include
                </span>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium tabular-nums ${allPdfSectionsIncluded ? "app-muted bg-[color:var(--app-hover)]" : "bg-sky-500/15 text-sky-700 dark:text-sky-300"}`}>
                  {allPdfSectionsIncluded ? "All sections" : `${includedPdfSectionIds.length} of ${pdfSectionOptions.length} included`}
                </span>
              </button>

              {pdfOptionsOpen && (
                <div className="app-divider space-y-3 border-t p-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="app-muted text-xs">Uncheck anything you don't want to share — handy for a customer-facing copy without internal details.</p>
                    <div className="flex shrink-0 items-center gap-1 text-xs">
                      <button type="button" className="app-link rounded px-1 py-0.5" onClick={() => setExcludedPdfSections([])}>
                        Select all
                      </button>
                      <span className="app-muted" aria-hidden="true">|</span>
                      <button
                        type="button"
                        className="app-link rounded px-1 py-0.5"
                        onClick={() => setExcludedPdfSections(pdfSectionOptions.map((section) => section.id))}
                      >
                        Deselect all
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {pdfSectionOptions.map((section) => {
                      const included = !excludedPdfSections.includes(section.id);
                      return (
                        <label
                          key={section.id}
                          className={`flex min-h-[2.75rem] cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-colors ${
                            included ? "border-sky-500/30 bg-sky-500/10" : "app-card app-muted"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 shrink-0 accent-sky-600"
                            checked={included}
                            onChange={() => togglePdfSection(section.id)}
                          />
                          <span className="min-w-0 truncate">{section.title}</span>
                        </label>
                      );
                    })}
                  </div>
                  {noPdfSections && (
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-300">Select at least one section to generate a PDF.</p>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {revisionModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-2 glass:backdrop-blur-[6px] sm:p-4"
          style={{ backgroundColor: "var(--app-scrim)" }}
        >
          <style>{`@keyframes cqStepFade{from{opacity:0}to{opacity:1}}`}</style>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cq-revision-title"
            className="app-glass app-glass-rim flex max-h-[94vh] w-full max-w-[95vw] flex-col overflow-hidden rounded-3xl border"
          >
            <div className="app-divider flex items-start justify-between gap-3 border-b px-5 py-4">
              <div className="min-w-0">
                <h3 id="cq-revision-title" className="text-lg font-semibold">Submit Quote Revision</h3>
                <p className="app-muted text-sm">Work through each step. Changes are tracked in the revision history.</p>
              </div>
              <button type="button" className="app-btn app-btn-secondary app-btn-sm shrink-0" onClick={closeRevisionModal} disabled={revisionSaving}>
                Close
              </button>
            </div>

            <div className="app-divider border-b px-5 py-3">
              <nav aria-label="Revision steps" className="flex items-center gap-2 overflow-x-auto">
                {revisionSteps.map((step, i) => {
                  const isCurrent = i === safeRevIndex;
                  return (
                    <button
                      key={step.id}
                      type="button"
                      onClick={() => setRevisionStepIndex(i)}
                      disabled={revisionSaving}
                      aria-current={isCurrent ? "step" : undefined}
                      className={stepPillClass(isCurrent)}
                    >
                      <span className={`flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold tabular-nums ${isCurrent ? "bg-white/25 text-white" : "bg-[color:var(--app-hover)]"}`}>
                        {i + 1}
                      </span>
                      <span className="whitespace-nowrap">{step.title}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            <form
              id="revisionForm"
              className="flex-1 overflow-y-auto p-4 sm:p-5"
              onSubmit={onSubmitRevision}
              onKeyDown={(event) => {
                if (event.key === "Enter" && event.target.tagName !== "TEXTAREA" && !isLastRevisionStep) {
                  event.preventDefault();
                }
              }}
            >
              <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:gap-6">
                <div className="w-full min-w-0 xl:flex-1">
                  <section className="app-surface rounded-2xl border p-5">
                    <div className="app-divider mb-5 flex items-center justify-between gap-3 border-b pb-4">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-500/15 text-sm font-bold tabular-nums text-sky-700 dark:text-sky-300">{safeRevIndex + 1}</span>
                        <div className="min-w-0">
                          <h4 className="truncate font-semibold">{currentRevisionStep?.title}</h4>
                          {currentRevisionStep?.hint && <p className="app-muted text-sm">{currentRevisionStep.hint}</p>}
                        </div>
                      </div>
                      {currentRevisionStep?.section?.repeatable && (
                        <button
                          type="button"
                          className="app-btn app-btn-secondary app-btn-sm shrink-0"
                          onClick={() => addRevisionRepeatableRow(currentRevisionStep.section.id)}
                          disabled={revisionSaving}
                        >
                          + Add Row
                        </button>
                      )}
                    </div>

                    <div key={safeRevIndex} style={{ animation: "cqStepFade 220ms ease-out" }}>
                      {renderActiveRevisionStep()}
                    </div>
                  </section>
                </div>

                <aside className="app-surface w-full rounded-2xl border p-4 xl:sticky xl:top-0 xl:w-80 xl:shrink-0">
                  <div className="mb-1 flex items-center gap-2">
                    <EditNoteIcon fontSize="small" className="app-muted" />
                    <h4 className="text-sm font-semibold">Notes — {currentRevisionStep?.title}</h4>
                  </div>
                  <p className="app-muted mb-3 text-xs">Notes for this step, saved on the quote for your reference.</p>
                  <textarea
                    className={`${FIELD_CLASS} h-56 resize-y`}
                    aria-label={`Notes for ${currentRevisionStep?.title || "this step"}`}
                    value={revisionSectionNotes[currentRevisionStep?.id] || ""}
                    onChange={(event) => {
                      const stepId = currentRevisionStep?.id;
                      if (!stepId) return;
                      setRevisionSectionNotes((prev) => ({ ...prev, [stepId]: event.target.value }));
                    }}
                    placeholder="Type here for notes…"
                    disabled={revisionSaving}
                  />
                </aside>
              </div>
            </form>

            <div className="app-divider flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3">
              <button
                type="button"
                className="app-btn app-btn-secondary"
                onClick={goPrevRevision}
                disabled={safeRevIndex === 0 || revisionSaving}
              >
                ← Back
              </button>

              <span className="app-muted hidden whitespace-nowrap text-xs tabular-nums sm:inline">Step {safeRevIndex + 1} of {revisionSteps.length}</span>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="app-btn app-btn-ghost"
                  onClick={closeRevisionModal}
                  disabled={revisionSaving}
                >
                  Cancel
                </button>
                {isLastRevisionStep ? (
                  <button className="app-btn app-btn-primary" type="submit" form="revisionForm" disabled={revisionSaving}>
                    {revisionSaving && <Spinner className="text-white" />}
                    {revisionSaving ? "Saving Revision..." : "Submit Revision"}
                  </button>
                ) : (
                  <button className="app-btn app-btn-primary" type="button" onClick={goNextRevision}>
                    Next →
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {checklistModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 glass:backdrop-blur-[6px]"
          style={{ backgroundColor: "var(--app-scrim)" }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cq-checklist-title"
            className="app-glass app-glass-rim max-h-[90vh] w-full max-w-2xl space-y-5 overflow-y-auto rounded-3xl border p-5 sm:p-6"
          >
            <div>
              <div className="flex items-center justify-between gap-3">
                <h3 id="cq-checklist-title" className="text-lg font-semibold">Checklist Templates</h3>
                <button type="button" className="app-btn app-btn-secondary app-btn-sm" onClick={() => setChecklistModalOpen(false)}>
                  Close
                </button>
              </div>
              <p className="app-muted mt-1 text-sm">
                A checklist tracks the steps to complete this quote. Pick a ready-made template to apply{isOwner ? ", or build your own reusable templates below" : ""}.
              </p>
            </div>

            <div className="app-surface space-y-3 rounded-2xl border p-4">
              <div>
                <h4 className="text-sm font-semibold">Step 1 · Apply a template to this quote</h4>
                {checklistTotal > 0 ? (
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-300">Heads up: this quote already has {checklistTotal} item{checklistTotal === 1 ? "" : "s"}. Applying a template replaces them.</p>
                ) : (
                  <p className="app-muted text-xs">The template's items become this quote's checklist.</p>
                )}
              </div>
              <select
                className={CQ_FIELD}
                aria-label="Checklist template"
                value={selectedTemplateId}
                onChange={(event) => setSelectedTemplateId(event.target.value)}
                disabled={saving}
              >
                {checklistTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                    {Array.isArray(template.items) ? ` (${template.items.length} item${template.items.length === 1 ? "" : "s"})` : ""}
                  </option>
                ))}
              </select>

              <div className="app-card rounded-xl border p-3">
                <p className="app-muted mb-1.5 text-xs font-medium">This template includes:</p>
                {selectedTemplateItems.length > 0 ? (
                  <ul className="space-y-1">
                    {selectedTemplateItems.map((item, index) => (
                      <li key={`${selectedTemplateId}_preview_${index}`} className="flex items-start gap-2 text-sm">
                        <span className="app-muted" aria-hidden="true">▢</span>
                        <span className="min-w-0">
                          {item?.label || "Untitled item"}
                          {item?.status_on_done && (
                            <span className="ml-1 inline-flex items-center rounded-full border border-sky-500/30 bg-sky-500/10 px-1.5 py-0.5 text-[11px] font-medium text-sky-700 dark:text-sky-300">
                              → {formatStatusLabel(item.status_on_done)}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="app-muted text-sm">No items in this template.</p>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <button className="app-btn app-btn-primary" type="button" onClick={onApplyTemplate} disabled={saving}>
                  {saving && <Spinner className="text-white" />}
                  Apply to Quote
                </button>
                {isOwner && (
                  <button
                    className="app-btn app-btn-secondary"
                    type="button"
                    onClick={onEditSelectedTemplate}
                    disabled={saving || isDefaultTemplateSelected}
                    title={isDefaultTemplateSelected ? "The default checklist can't be edited" : "Load this template into the editor below"}
                  >
                    Edit in Editor
                  </button>
                )}
              </div>
            </div>

            {isOwner && (
              <form className="app-divider space-y-3 border-t pt-5" onSubmit={onCreateTemplate}>
                <h4 className="text-sm font-semibold">Step 2 · {editingTemplateId ? "Edit a template" : "Build your own template"}</h4>
                {editingTemplateId ? (
                  <Notice tone="amber" className="flex flex-wrap items-center justify-between gap-2 !text-xs">
                    <span>Editing "{newTemplateName || "Untitled template"}". Save to update it, or cancel to start fresh.</span>
                    <button type="button" className="whitespace-nowrap font-semibold underline" onClick={onCancelTemplateEdit} disabled={saving}>
                      Cancel edit
                    </button>
                  </Notice>
                ) : (
                  <p className="app-muted text-xs">Reusable across your quotes. Optionally, a line can advance the quote's status when it's checked off.</p>
                )}
                <div>
                  <label htmlFor="cq-new-template-name" className="mb-1 block text-xs font-medium">Template name</label>
                  <input
                    id="cq-new-template-name"
                    className={CQ_FIELD}
                    value={newTemplateName}
                    onChange={(event) => setNewTemplateName(event.target.value)}
                    placeholder="e.g. Kitchen remodel checklist"
                    disabled={saving}
                  />
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-medium">Checklist items</p>
                  {newTemplateItems.map((item, index) => (
                    <div key={item.id} className="app-surface space-y-2 rounded-xl border p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold tabular-nums">Item {index + 1}</p>
                        <button
                          type="button"
                          className="app-btn app-btn-ghost app-btn-sm !text-rose-700 dark:!text-rose-300"
                          onClick={() => onTemplateItemRemove(item.id)}
                          disabled={newTemplateItems.length <= 1 || saving}
                        >
                          Remove
                        </button>
                      </div>
                      <input
                        className={CQ_FIELD}
                        aria-label={`Checklist item ${index + 1}`}
                        value={item.label}
                        onChange={(event) => onTemplateItemChange(item.id, { label: event.target.value })}
                        placeholder="What needs to be done? (e.g. Measure kitchen)"
                        disabled={saving}
                      />
                      <div>
                        <label htmlFor={`cq-item-status-${item.id}`} className="app-muted mb-1 block text-[11px]">When this item is checked off (optional):</label>
                        <select
                          id={`cq-item-status-${item.id}`}
                          className={CQ_FIELD}
                          value={item.status_on_done || ""}
                          onChange={(event) => onTemplateItemChange(item.id, { status_on_done: event.target.value })}
                          disabled={saving}
                        >
                          <option value="">Don't change the quote status</option>
                          {quoteStatuses.map((status) => (
                            <option key={status} value={status}>
                              Move quote to "{formatStatusLabel(status)}"
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2 pt-1">
                  <button className="app-btn app-btn-secondary" type="button" onClick={onTemplateItemAdd} disabled={saving}>
                    + Add Checklist Item
                  </button>

                  <button className="app-btn app-btn-primary" type="submit" disabled={saving}>
                    {saving && <Spinner className="text-white" />}
                    {editingTemplateId ? "Update Template" : "Save Template"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {imageModal.open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 glass:backdrop-blur-[6px]"
          onClick={() => setImageModal({ open: false, src: "", name: "" })}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={imageModal.name}
            className="app-glass app-glass-rim w-full max-w-5xl space-y-3 rounded-3xl border p-3 sm:p-4"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3">
              <p className="truncate text-sm font-medium">{imageModal.name}</p>
              <div className="flex shrink-0 items-center gap-2">
                <a
                  href={withDownloadQuery(imageModal.src)}
                  target="_blank"
                  rel="noreferrer"
                  className="app-btn app-btn-primary app-btn-sm"
                >
                  Download
                </a>
                <button
                  type="button"
                  className="app-btn app-btn-secondary app-btn-sm"
                  onClick={() => setImageModal({ open: false, src: "", name: "" })}
                >
                  Close
                </button>
              </div>
            </div>
            <img src={imageModal.src} alt={imageModal.name || "Attached"} className="app-divider max-h-[75vh] w-full rounded-2xl border object-contain" />
          </div>
        </div>
      )}
    </PageShell>
  );
}

export default CabinetQuoteWorkspace;
